import React, { useState, useCallback, useEffect } from "react";
import { Modal, ModalBody } from "reactstrap";
import VerifyEmail from "../SignUp/VerifyEmail";
import VerifyMobile from "../../../helpers/Common/VerifyMobile";
import { forgotEmailPassword, resendMobileOtp } from "../../../api/customer";
import { isEmailVerified, isMobileVerified, detectLoginMethod } from "../../../helpers/common";
import "./VerifyEmailAndMobileModal.css";

const VerifyEmailAndMobileModal = ({ openModal, setOpenModal, formData, userData }) => {
  const [currentStep, setCurrentStep] = useState("mobile"); // "mobile" or "email"
  const [mobileVerified, setMobileVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  
  const toggleModal = () => setOpenModal(!openModal);

  // Determine which verifications are needed
  const needsEmailVerification = userData && !isEmailVerified(userData);
  const needsMobileVerification = userData && !isMobileVerified(userData);

  // Set initial step based on what needs verification
  useEffect(() => {
    if (needsMobileVerification) {
      setCurrentStep("mobile");
    } else if (needsEmailVerification) {
      setCurrentStep("email");
    }
  }, [needsMobileVerification, needsEmailVerification]);

  const sendEmailOtp = useCallback(() => {
    if (openModal && needsEmailVerification) {
      // Extract email from username if it's an email, otherwise use the username
      const email = detectLoginMethod(formData?.username) === "email" ? formData?.username : userData?.customer?.email;
      forgotEmailPassword({ email })
        .then((result) => {
          console.log("Email OTP sent:", result);
        })
        .catch((err) => {
          console.log("Email OTP error:", err);
        });
    }
  }, [formData?.username, openModal, needsEmailVerification, userData]);

  const sendMobileOtp = useCallback(() => {
    if (openModal && needsMobileVerification && userData?.customer?.phoneNumber) {
      resendMobileOtp({ phoneNumber: userData?.customer?.phoneNumber })
        .then((result) => {
          console.log("Mobile OTP sent:", result);
        })
        .catch((err) => {
          console.log("Mobile OTP error:", err);
        });
    }
  }, [openModal, needsMobileVerification, userData]);

  useEffect(() => {
    if (currentStep === "mobile" && needsMobileVerification) {
      sendMobileOtp();
    } else if (currentStep === "email" && needsEmailVerification) {
      sendEmailOtp();
    }
  }, [currentStep, sendMobileOtp, sendEmailOtp, needsMobileVerification, needsEmailVerification]);

  const handleMobileVerificationSuccess = () => {
    setMobileVerified(true);
    if (needsEmailVerification) {
      setCurrentStep("email");
    } else {
      // Both verifications are complete, close modal
      toggleModal();
    }
  };

  const handleEmailVerificationSuccess = () => {
    setEmailVerified(true);
    // Both verifications are complete, close modal
    toggleModal();
  };

  const renderStepIndicator = () => {
    const showMobileStep = needsMobileVerification;
    const showEmailStep = needsEmailVerification;

    // Only show stepper when both verifications are needed
    if (!showMobileStep || !showEmailStep) return null;

    return (
      <div className="stepper-container">
        <div className={`step-indicator ${currentStep === "mobile" ? "active" : mobileVerified ? "completed" : ""}`}>
          <div className="step-number">1</div>
          <div className="step-label">Mobile</div>
        </div>
        <div className={`step-line ${mobileVerified ? "completed" : ""}`}></div>
        <div className={`step-indicator ${currentStep === "email" ? "active" : emailVerified ? "completed" : ""}`}>
          <div className="step-number">2</div>
          <div className="step-label">Email</div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (currentStep === "mobile" && needsMobileVerification) {
      return (
        <VerifyMobile
          formData={{ phoneNumber: userData?.customer?.phoneNumber }}
          onVerifyMobileSuccess={handleMobileVerificationSuccess}
        />
      );
    } else if (currentStep === "email" && needsEmailVerification) {
      // Create formData with email for VerifyEmail component
      const emailFormData = {
        ...formData,
        email: detectLoginMethod(formData?.username) === "email" ? formData?.username : userData?.customer?.email
      };
      
      return (
        <VerifyEmail
          source='login'
          mode='light'
          formData={emailFormData}
          showLoginBtn={false}
          showFullDescription={true}
          onVerifyEmailSuccess={handleEmailVerificationSuccess}
        />
      );
    }
    return null;
  };

  if (!formData?.username || !userData) {
    return null;
  }

  return (
    <Modal isOpen={openModal} toggle={toggleModal} centered size='md'>
      <ModalBody className='modal-body p-5' style={{ backgroundColor: "#493ef0" }}>
        {renderStepIndicator()}
        {renderContent()}
      </ModalBody>
    </Modal>
  );
};

export default VerifyEmailAndMobileModal; 